import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ApiError, api } from "@/api/client";
import { useDeal } from "@/api/crm";
import { useCountries, useMe } from "@/api/hooks";
import { resolvePrice } from "@/api/commerce";
import { openPdf, useCompany, useQuote, useQuoteAction } from "@/api/sales";
import type { ProductUnit, Quote } from "@/api/types";
import { findCountry } from "@/components/crm/LocationFields";
import { RecordPicker, type PickerValue } from "@/components/crm/RecordPicker";
import { Icon } from "@/components/icons";
import { Alert, Modal, PageHeader, Spinner, formatDate } from "@/components/ui";
import { money, shortDate } from "@/lib/format";
import { can } from "@/lib/permissions";
import { lineMath, totals } from "@/lib/pricing";
import { EsignPanel, PaymentPanel } from "@/features/orders/CollectPanels";
import { QuoteStatusBadge } from "./QuoteStatusBadge";
import { TaxNote, taxLines } from "./tax";
import { SendQuoteDialog } from "./SendQuoteDialog";

interface Row {
  key: string; product: PickerValue | null; description: string; hsn_code: string; uom: string; quantity: string; unit_price: string; discount_pct: string; tax_rate: string;
  /** CPQ: where the rate came from, the product's units, and whether the rate is still the automatic one. */
  source?: string | null; units?: ProductUnit[]; auto?: boolean;
}
const n = (s: string) => (s.trim() === "" || Number.isNaN(Number(s)) ? 0 : Number(s));
let rowSeq = 0;
const emptyRow = (taxRate = ""): Row => ({ key: `r${++rowSeq}`, product: null, description: "", hsn_code: "", uom: "pcs", quantity: "1", unit_price: "", discount_pct: "0", tax_rate: taxRate });

function rowsFrom(quote?: Quote): Row[] {
  if (!quote?.lines.length) return [emptyRow()];
  return quote.lines.map((l) => ({
    key: `r${++rowSeq}`, product: l.product_id ? { id: l.product_id, name: l.description } : null, description: l.description, hsn_code: l.hsn_code ?? "", uom: l.uom,
    quantity: String(l.quantity), unit_price: String(l.unit_price), discount_pct: String(l.discount_pct), tax_rate: String(l.tax_rate),
    source: l.price_source ?? null, auto: l.price_source != null && l.list_price === l.unit_price,
  }));
}

function TotalsBox({ currency, gross, discount, net, lines }: { currency: string; gross: number; discount: number; net: number; lines: [string, number][] }) {
  const m = (v: number) => money(v, currency);
  return (
    <dl className="ml-auto w-full max-w-sm space-y-1.5 text-sm" aria-label="Totals">
      {discount > 0 && <><div className="flex justify-between"><dt className="text-muted">Gross</dt><dd className="tabular-nums">{m(gross)}</dd></div>
        <div className="flex justify-between"><dt className="text-muted">Discount</dt><dd className="tabular-nums">−{m(discount)}</dd></div></>}
      <div className="flex justify-between"><dt className="text-muted">Taxable value</dt><dd className="tabular-nums">{m(net)}</dd></div>
      {lines.map(([label, v]) => <div key={label} className="flex justify-between"><dt className="text-muted">{label}</dt><dd className="tabular-nums">{m(v)}</dd></div>)}
    </dl>
  );
}

function Editor({ quote, dealId }: { quote?: Quote; dealId?: string }) {
  const navigate = useNavigate();
  const company = useCompany();
  const countries = useCountries();
  const sellerCountry = findCountry(countries.data, company.data?.country);
  const codeLabel = quote?.item_code_label ?? sellerCountry?.item_code_label ?? "HS code";
  const defaultRate = sellerCountry ? String(Number(sellerCountry.standard_rate)) : "";
  const deal = useDeal(dealId ?? "");
  const action = useQuoteAction();
  const fromDeal = dealId ? deal.data : undefined;
  const [title, setTitle] = useState(quote?.title ?? "");
  const [account, setAccount] = useState<PickerValue | null>(quote?.account ?? null);
  const [contact, setContact] = useState<PickerValue | null>(quote?.contact ? { id: quote.contact.id, name: quote.contact.full_name } : null);
  const [validUntil, setValidUntil] = useState(quote?.valid_until ?? "");
  const [payment, setPayment] = useState(quote?.payment_terms ?? "");
  const [delivery, setDelivery] = useState(quote?.delivery_terms ?? "");
  const [notes, setNotes] = useState(quote?.notes ?? "");
  const [rows, setRows] = useState<Row[]>(() => rowsFrom(quote));
  const [dirty, setDirty] = useState(!quote);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const currency = quote?.currency ?? fromDeal?.currency ?? company.data?.currency ?? "INR";

  // A brand-new quote's first blank line starts at the standard rate of the company's country.
  useEffect(() => {
    if (defaultRate) setRows((rs) => rs.map((r) => (r.tax_rate === "" && !r.product && !r.description ? { ...r, tax_rate: defaultRate } : r)));
  }, [defaultRate]);
  const update = (key: string, patch: Partial<Row>) => { setDirty(true); setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r))); };
  const customerId = account?.id ?? fromDeal?.account?.id ?? null;
  /** Ask CPQ for this customer's price (price book, quantity tier, unit) and fill the rate. */
  const reprice = (key: string, productId: string, quantity: string, uom: string | null) => {
    void resolvePrice({ product_id: productId, quantity, uom }, customerId, currency).then((p) =>
      setRows((rs) => rs.map((r) => (r.key === key ? { ...r, unit_price: String(p.unit_price), uom: p.uom, source: p.source, units: p.units, auto: true } : r))),
    ).catch(() => undefined);
  };
  const preview = useMemo(() => rows.map((r) => lineMath({ quantity: n(r.quantity), unit_price: n(r.unit_price), discount_pct: n(r.discount_pct), tax_rate: n(r.tax_rate) })), [rows]);
  const sum = totals(rows.map((r) => ({ quantity: n(r.quantity), unit_price: n(r.unit_price), discount_pct: n(r.discount_pct), tax_rate: n(r.tax_rate) })));

  const save = async (): Promise<Quote | null> => {
    setErrors({}); setError(null);
    const lines = rows.filter((r) => r.description.trim() || r.product).map((r) => ({
      product_id: r.product?.id ?? null, description: r.description.trim() || null, quantity: r.quantity || "0",
      unit_price: r.unit_price === "" ? null : r.unit_price, discount_pct: r.discount_pct || "0", tax_rate: r.tax_rate || "0",
      uom: r.uom || null, hsn_code: r.hsn_code.trim() || null,
    }));
    const body = {
      title: title.trim() || null, account_id: account?.id ?? null, contact_id: contact?.id ?? null,
      valid_until: validUntil || null, payment_terms: payment.trim() || null, delivery_terms: delivery.trim() || null,
      notes: notes.trim() || null, lines, ...(quote ? {} : { deal_id: dealId ?? null }),
    };
    try {
      const saved = await action.mutateAsync(quote ? { path: `/quotes/${quote.id}`, method: "PATCH", body } : { path: "/quotes", body });
      setDirty(false);
      if (!quote) navigate(`/quotes/${saved.id}`, { replace: true });
      return saved;
    } catch (e) {
      if (e instanceof ApiError && e.problem.errors?.length) {
        const fe = e.fieldErrors();
        setErrors(fe);
        setError(fe.title ? "Give the quote a subject, or create it from a deal." : "Some lines need attention.");
      } else setError(e instanceof Error ? e.message : "Could not save");
      return null;
    }
  };

  const lineError = (i: number, field: string) => errors[`lines.${i}.${field}`];

  return (
    <div className="space-y-6">
      {error && <Alert>{error}</Alert>}
      <section className="card grid gap-4 p-5 sm:grid-cols-2" aria-label="Quote details">
        <div className="sm:col-span-2"><label htmlFor="q-title" className="label">Subject</label>
          <input id="q-title" className="input" value={title} placeholder={fromDeal?.name ?? "e.g. Hitch brackets, 8k pcs per month"} onChange={(e) => { setTitle(e.target.value); setDirty(true); }} />
          {fromDeal && !quote && <p className="mt-1 text-xs text-muted">For deal <Link className="text-brand hover:underline" to={`/deals/${fromDeal.id}`}>{fromDeal.name}</Link>; customer and contact come from the deal unless you choose others.</p>}
        </div>
        <RecordPicker label="Customer account" endpoint="/accounts" value={account ?? (fromDeal?.account ?? null)} onChange={(v) => { setAccount(v); setDirty(true); }} />
        <RecordPicker label="Contact" endpoint="/contacts" value={contact ?? (fromDeal?.contact ? { id: fromDeal.contact.id, name: fromDeal.contact.full_name } : null)} onChange={(v) => { setContact(v); setDirty(true); }} />
        <div><label htmlFor="q-valid" className="label">Valid until</label><input id="q-valid" type="date" className="input" value={validUntil} onChange={(e) => { setValidUntil(e.target.value); setDirty(true); }} />
          {!validUntil && <p className="mt-1 text-xs text-muted">Defaults to {company.data?.quote_validity_days ?? 30} days from today</p>}</div>
        <div><label htmlFor="q-payment" className="label">Payment terms</label><input id="q-payment" className="input" placeholder="e.g. 30% advance, balance 45 days" value={payment} onChange={(e) => { setPayment(e.target.value); setDirty(true); }} /></div>
        <div className="sm:col-span-2"><label htmlFor="q-delivery" className="label">Delivery terms</label><input id="q-delivery" className="input" placeholder="e.g. 4 weeks from PO, ex-works" value={delivery} onChange={(e) => { setDelivery(e.target.value); setDirty(true); }} /></div>
      </section>

      <section className="card overflow-x-auto" aria-label="Line items">
        <table className="w-full min-w-[56rem] text-left text-sm">
          <thead className="border-b border-line bg-canvas text-xs uppercase tracking-wide text-muted">
            <tr><th className="px-3 py-2 w-[30%]">Item</th><th className="px-2 py-2">{codeLabel}</th><th className="px-2 py-2 text-right">Qty</th><th className="px-2 py-2">Unit</th><th className="px-2 py-2 text-right">Rate</th><th className="px-2 py-2 text-right">Disc. %</th><th className="px-2 py-2 text-right">{sellerCountry?.tax_name ?? "Tax"} %</th><th className="px-3 py-2 text-right">Amount</th><th className="px-2 py-2"><span className="sr-only">Remove</span></th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.key} className="border-b border-line align-top last:border-0">
                <td className="px-3 py-2">
                  <RecordPicker hideLabel label={`Line ${i + 1} product`} endpoint="/products" value={r.product} placeholder="Search catalog…"
                    onChange={(v) => update(r.key, { product: v })}
                    onPickRaw={(p) => { update(r.key, { description: String(p.name), unit_price: String(p.unit_price), tax_rate: String(p.tax_rate), uom: String(p.uom), hsn_code: String(p.hsn_code ?? ""), source: null }); reprice(r.key, String(p.id), r.quantity, String(p.uom)); }} />
                  <input className="input mt-1" aria-label={`Line ${i + 1} description`} placeholder="Or describe the item" value={r.description} onChange={(e) => update(r.key, { description: e.target.value })} />
                  {lineError(i, "description") && <p className="field-error">Describe the item or pick a product</p>}
                </td>
                <td className="px-2 py-2"><input className="input w-24" aria-label={`Line ${i + 1} ${codeLabel}`} value={r.hsn_code} onChange={(e) => update(r.key, { hsn_code: e.target.value })} /></td>
                <td className="px-2 py-2"><input className="input w-24 text-right" type="number" min={0} step="any" aria-label={`Line ${i + 1} quantity`} value={r.quantity} onChange={(e) => update(r.key, { quantity: e.target.value })} onBlur={() => { if (r.product && r.auto) reprice(r.key, r.product.id, r.quantity, r.uom); }} />{lineError(i, "quantity") && <p className="field-error">{lineError(i, "quantity")}</p>}</td>
                <td className="px-2 py-2">{r.product && r.units && r.units.length > 1
                  ? <select className="input w-28" aria-label={`Line ${i + 1} unit`} value={r.uom} onChange={(e) => { update(r.key, { uom: e.target.value }); reprice(r.key, r.product!.id, r.quantity, e.target.value); }}>
                      {r.units.map((u) => <option key={u.uom} value={u.uom} title={u.factor !== 1 ? `1 ${u.uom} = ${u.factor} ${r.units?.[0]?.uom ?? ""}` : undefined}>{u.uom}{u.factor !== 1 ? ` ×${u.factor}` : ""}</option>)}
                    </select>
                  : <input className="input w-16" aria-label={`Line ${i + 1} unit`} value={r.uom} onChange={(e) => update(r.key, { uom: e.target.value })} />}</td>
                <td className="px-2 py-2"><input className="input w-28 text-right" type="number" min={0} step="0.01" aria-label={`Line ${i + 1} rate`} value={r.unit_price} onChange={(e) => update(r.key, { unit_price: e.target.value, auto: false })} />{lineError(i, "unit_price") && <p className="field-error">Required</p>}
                  {r.source && <p className="mt-1 max-w-28 text-[11px] leading-tight text-muted" data-testid={`price-source-${i + 1}`}>{r.auto === false ? "Changed by hand" : r.source}</p>}</td>
                <td className="px-2 py-2"><input className="input w-20 text-right" type="number" min={0} max={100} step="0.01" aria-label={`Line ${i + 1} discount`} value={r.discount_pct} onChange={(e) => update(r.key, { discount_pct: e.target.value })} /></td>
                <td className="px-2 py-2"><input className="input w-20 text-right" type="number" min={0} max={100} step="0.01" aria-label={`Line ${i + 1} tax rate`} value={r.tax_rate} onChange={(e) => update(r.key, { tax_rate: e.target.value })} /></td>
                <td className="whitespace-nowrap px-3 py-2 pt-4 text-right font-medium tabular-nums">{money(preview[i]?.total ?? 0, currency)}</td>
                <td className="px-2 py-2 pt-3"><button type="button" className="btn-ghost px-2" aria-label={`Remove line ${i + 1}`} disabled={rows.length === 1} onClick={() => { setRows((rs) => rs.filter((x) => x.key !== r.key)); setDirty(true); }}>✕</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex flex-wrap items-start justify-between gap-4 border-t border-line p-4">
          <button type="button" className="btn-ghost" onClick={() => { setRows((rs) => [...rs, emptyRow(defaultRate)]); setDirty(true); }}><Icon name="plus" />Add line</button>
          <div className="w-full max-w-sm">
            <TotalsBox currency={currency} gross={sum.gross} discount={sum.discount} net={sum.net}
              lines={!dirty && quote ? taxLines(quote) : [[`${sellerCountry?.tax_name ?? "Tax"} (estimate)`, sum.tax]]} />
            <div className="ml-auto mt-2 flex max-w-sm justify-between border-t-2 border-brand pt-2 text-base font-semibold"><span>Total ({currency})</span><span className="tabular-nums" data-testid="quote-total">{money(!dirty && quote ? quote.grand_total : sum.total, currency)}</span></div>
            {dirty && <p className="mt-1 text-right text-xs text-muted">The exact tax treatment (for example CGST + SGST or IGST, reverse charge or zero-rated export) is worked out from your company's and the customer's country, state and tax ID when you save.</p>}
            {!dirty && quote && <TaxNote note={quote.tax_note} label={quote.tax_mode_label} />}
          </div>
        </div>
      </section>

      <section className="card p-5" aria-label="Notes">
        <label htmlFor="q-notes" className="label">Notes to the customer</label>
        <textarea id="q-notes" className="input min-h-20" value={notes} onChange={(e) => { setNotes(e.target.value); setDirty(true); }} placeholder="Shown on the quote, e.g. drawing reference or packing details" />
        <p className="mt-2 text-xs text-muted">Standard terms and conditions come from <Link to="/settings/company" className="text-brand hover:underline">Company profile</Link>.</p>
      </section>

      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className="btn-primary" disabled={action.isPending} onClick={() => void save()}>{quote ? "Save draft" : "Create quote"}</button>
      </div>
    </div>
  );
}

function DecisionDialog({ quote, onClose }: { quote: Quote; onClose: () => void }) {
  const action = useQuoteAction();
  const [decision, setDecision] = useState<"accepted" | "rejected">("accepted");
  const [name, setName] = useState(quote.contact?.full_name ?? "");
  const [comment, setComment] = useState("");
  return (
    <Modal title="Record the customer's decision" onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void action.mutateAsync({ path: `/quotes/${quote.id}/decision`, body: { decision, name, comment: comment || null } }).then(onClose); }}>
        <div className="flex gap-2" role="radiogroup" aria-label="Decision">
          {(["accepted", "rejected"] as const).map((d) => (
            <button key={d} type="button" role="radio" aria-checked={decision === d} onClick={() => setDecision(d)}
              className={`btn flex-1 border ${decision === d ? (d === "accepted" ? "border-ok bg-ok/10 text-ok" : "border-danger bg-danger/10 text-danger") : "border-line"}`}>{d === "accepted" ? "Accepted" : "Declined"}</button>
          ))}
        </div>
        <div><label htmlFor="dec-name" className="label">Decided by</label><input id="dec-name" className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div><label htmlFor="dec-comment" className="label">Comment</label><input id="dec-comment" className="input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="e.g. Confirmed by phone, PO to follow" /></div>
        <div className="flex justify-end gap-2"><button type="button" className="btn-ghost" onClick={onClose}>Cancel</button><button type="submit" className="btn-primary" disabled={!name.trim()}>Save decision</button></div>
      </form>
    </Modal>
  );
}

function ApprovalPanel({ quote }: { quote: Quote }) {
  const perms = useMe().data?.permissions ?? [];
  const action = useQuoteAction();
  const [comment, setComment] = useState("");
  const [error, setError] = useState<string | null>(null);
  const status = quote.approval_status;
  if (status === "not_required" || quote.status !== "draft") {
    return status === "approved" && quote.approver_name ? <Alert tone="ok">Approved by {quote.approver_name}{quote.approval_comment && `: “${quote.approval_comment}”`}</Alert> : null;
  }
  const run = (path: string, body: Record<string, unknown>) => {
    setError(null);
    void action.mutateAsync({ path, body }).then(() => setComment("")).catch((e: Error) => setError(e.message));
  };
  const approver = can(perms, "quotes:approve");
  return (
    <section className={`card border-l-4 p-5 ${status === "approved" ? "border-l-ok" : status === "rejected" ? "border-l-danger" : "border-l-warn"}`} aria-label="Approval">
      <h2 className="font-semibold">
        {status === "required" && "Needs a manager's approval before sending"}
        {status === "pending" && "Waiting for approval"}
        {status === "approved" && `Approved by ${quote.approver_name ?? "a manager"}`}
        {status === "rejected" && `Approval declined by ${quote.approver_name ?? "a manager"}`}
      </h2>
      {status !== "approved" && <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted">{quote.approval_reasons.map((r) => <li key={r}>{r}</li>)}</ul>}
      {quote.approval_comment && <p className="mt-2 text-sm">“{quote.approval_comment}”</p>}
      {error && <div className="mt-3"><Alert>{error}</Alert></div>}
      {(status === "required" || status === "rejected") && can(perms, "quotes:update") && (
        <div className="mt-3 flex flex-wrap gap-2">
          <input className="input max-w-md flex-1" aria-label="Note for the approver" placeholder="Why this price? (optional)" value={comment} onChange={(e) => setComment(e.target.value)} />
          <button type="button" className="btn-primary" disabled={action.isPending} onClick={() => run(`/quotes/${quote.id}/request-approval`, { comment: comment || null })}>Ask for approval</button>
        </div>
      )}
      {status === "pending" && approver && (
        <div className="mt-3 flex flex-wrap gap-2">
          <input className="input max-w-md flex-1" aria-label="Approval comment" placeholder="Comment (optional)" value={comment} onChange={(e) => setComment(e.target.value)} />
          <button type="button" className="btn-primary" disabled={action.isPending} onClick={() => run(`/quotes/${quote.id}/approval`, { decision: "approved", comment: comment || null })}>Approve</button>
          <button type="button" className="btn-danger" disabled={action.isPending} onClick={() => run(`/quotes/${quote.id}/approval`, { decision: "rejected", comment: comment || null })}>Decline</button>
        </div>
      )}
      {status === "pending" && !approver && <p className="mt-2 text-sm text-muted">Your managers have been emailed. You can send the quote once it is approved.</p>}
    </section>
  );
}

function OrderDialog({ quote, onClose }: { quote: Quote; onClose: () => void }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ customer_po: "", customer_po_date: "", deliver_by: "" });
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const submit = async () => {
    setError(null); setPending(true);
    try {
      const order = await api<{ id: string }>("POST", `/quotes/${quote.id}/order`, {
        customer_po: form.customer_po.trim() || null, customer_po_date: form.customer_po_date || null, deliver_by: form.deliver_by || null,
      });
      navigate(`/orders/${order.id}`);
    } catch (e) { setError(e instanceof Error ? e.message : "Could not create the order"); setPending(false); }
  };
  return (
    <Modal title="Create sales order" onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <p className="text-sm text-muted">The order copies this quote's lines, prices and taxes. If an accounting system is connected, it is sent there automatically.</p>
        {error && <Alert>{error}</Alert>}
        <div className="grid gap-3 sm:grid-cols-3">
          <div><label htmlFor="o-po" className="label">Customer PO number</label><input id="o-po" className="input" value={form.customer_po} onChange={(e) => setForm({ ...form, customer_po: e.target.value })} /></div>
          <div><label htmlFor="o-po-date" className="label">PO date</label><input id="o-po-date" type="date" className="input" value={form.customer_po_date} onChange={(e) => setForm({ ...form, customer_po_date: e.target.value })} /></div>
          <div><label htmlFor="o-deliver" className="label">Deliver by</label><input id="o-deliver" type="date" className="input" value={form.deliver_by} onChange={(e) => setForm({ ...form, deliver_by: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-2"><button type="button" className="btn-ghost" onClick={onClose}>Cancel</button><button type="submit" className="btn-primary" disabled={pending}>Create order</button></div>
      </form>
    </Modal>
  );
}

function View({ quote }: { quote: Quote }) {
  const navigate = useNavigate();
  const perms = useMe().data?.permissions ?? [];
  const action = useQuoteAction();
  const [deciding, setDeciding] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [copied, setCopied] = useState(false);
  const canEdit = can(perms, "quotes:update");
  const open = quote.display_status === "sent";
  return (
    <div className="space-y-6">
      {quote.display_status === "accepted" && <Alert tone="ok">Accepted by {quote.decided_by_name} on {formatDate(quote.decided_at!)}{quote.customer_comment && `: “${quote.customer_comment}”`}</Alert>}
      {quote.display_status === "rejected" && <Alert>Declined by {quote.decided_by_name}{quote.customer_comment && `: “${quote.customer_comment}”`}</Alert>}
      {quote.display_status === "expired" && <Alert tone="warn">This quote expired on {shortDate(quote.valid_until)}. Create a revision to send updated prices.</Alert>}
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn-ghost" onClick={() => void openPdf(`/quotes/${quote.id}/pdf`)}><Icon name="download" />PDF</button>
        <button type="button" className="btn-ghost" onClick={() => { void navigator.clipboard?.writeText(quote.public_url); setCopied(true); }}><Icon name="link" />{copied ? "Link copied" : "Copy customer link"}</button>
        <a className="btn-ghost" href={quote.public_url} target="_blank" rel="noopener noreferrer">Customer view</a>
        {canEdit && quote.is_latest && open && <button type="button" className="btn-ghost" onClick={() => setDeciding(true)}>Record decision</button>}
        {quote.display_status === "accepted" && quote.order_id && <Link className="btn-primary" to={`/orders/${quote.order_id}`}><Icon name="order" />View sales order</Link>}
        {quote.display_status === "accepted" && !quote.order_id && quote.is_latest && can(perms, "orders:create") &&
          <button type="button" className="btn-primary" onClick={() => setOrdering(true)}><Icon name="order" />Create sales order</button>}
        {canEdit && quote.is_latest && ["sent", "rejected", "expired"].includes(quote.display_status) &&
          <button type="button" className="btn-primary" onClick={() => void action.mutateAsync({ path: `/quotes/${quote.id}/revise` }).then((q) => navigate(`/quotes/${q.id}`))}>Revise quote</button>}
      </div>
      <section className="card overflow-x-auto" aria-label="Line items">
        <table className="w-full min-w-[48rem] text-left text-sm">
          <thead className="border-b border-line bg-canvas text-xs uppercase tracking-wide text-muted">
            <tr><th className="px-4 py-2">#</th><th className="px-2 py-2">Item</th><th className="px-2 py-2">{quote.item_code_label}</th><th className="px-2 py-2 text-right">Qty</th><th className="px-2 py-2 text-right">Rate</th><th className="px-2 py-2 text-right">Disc.</th><th className="px-2 py-2 text-right">Taxable</th><th className="px-2 py-2 text-right">Tax rate</th><th className="px-4 py-2 text-right">Amount</th></tr>
          </thead>
          <tbody>
            {quote.lines.map((l, i) => (
              <tr key={l.id} className="border-b border-line last:border-0">
                <td className="px-4 py-2 text-muted">{i + 1}</td>
                <td className="px-2 py-2"><span className="font-medium">{l.description}</span>{l.sku && <span className="block text-xs text-muted">{l.sku}</span>}
                  {(l.price_source || l.margin_pct != null) && <span className="block text-xs text-muted">{[l.price_source, l.margin_pct != null && `margin ${l.margin_pct}%`].filter(Boolean).join(" · ")}</span>}</td>
                <td className="px-2 py-2 text-muted">{l.hsn_code}</td>
                <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">{l.quantity} {l.uom}</td>
                <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">{money(l.unit_price, quote.currency)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{l.discount_pct}%</td>
                <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">{money(l.net_amount, quote.currency)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{l.tax_rate}%</td>
                <td className="whitespace-nowrap px-4 py-2 text-right font-medium tabular-nums">{money(l.line_total, quote.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="border-t border-line p-4">
          <TotalsBox currency={quote.currency} gross={quote.gross} discount={quote.discount_total} net={quote.subtotal} lines={taxLines(quote)} />
          <div className="ml-auto mt-2 flex max-w-sm justify-between border-t-2 border-brand pt-2 text-base font-semibold"><span>Total ({quote.currency})</span><span className="tabular-nums" data-testid="quote-total">{money(quote.grand_total, quote.currency)}</span></div>
          <TaxNote note={quote.tax_note} label={quote.tax_mode_label} />
        </div>
      </section>
      {deciding && <DecisionDialog quote={quote} onClose={() => setDeciding(false)} />}
      {ordering && <OrderDialog quote={quote} onClose={() => setOrdering(false)} />}
    </div>
  );
}

export function QuotePage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const perms = useMe().data?.permissions ?? [];
  const quote = useQuote(id);
  const [sending, setSending] = useState(false);

  if (!id) return <><PageHeader title="New quote" /><Editor dealId={params.get("deal") ?? undefined} /></>;
  if (quote.isLoading) return <Spinner />;
  if (quote.error) {
    const notFound = quote.error instanceof ApiError && quote.error.status === 404;
    return <Alert>{notFound ? "This quote does not exist or you do not have access to it." : quote.error.message}</Alert>;
  }
  const q = quote.data!;
  const isDraft = q.status === "draft";
  const canEdit = can(perms, "quotes:update");
  const needsApproval = ["required", "pending", "rejected"].includes(q.approval_status);

  return (
    <>
      <Link to={q.deal ? `/deals/${q.deal.id}` : "/quotes"} className="mb-4 inline-block text-sm text-muted hover:text-brand">← {q.deal ? q.deal.name : "All quotes"}</Link>
      <PageHeader title={`${q.number}${q.version > 1 ? ` rev. ${q.version}` : ""}`}
        subtitle={[q.title, q.account?.name, `Valid until ${shortDate(q.valid_until)}`].filter(Boolean).join(" · ")}
        actions={<>
          <QuoteStatusBadge status={q.is_latest ? q.display_status : "superseded"} />
          {isDraft && canEdit && <button type="button" className="btn-ghost" onClick={() => void openPdf(`/quotes/${q.id}/pdf`)}><Icon name="download" />Preview PDF</button>}
          {isDraft && canEdit && <button type="button" className="btn-primary" disabled={!q.lines.length || needsApproval} title={needsApproval ? "Needs approval first" : q.lines.length ? undefined : "Add at least one line first"} onClick={() => setSending(true)}><Icon name="send" />Send</button>}
          {isDraft && can(perms, "quotes:delete") && <button type="button" className="btn-ghost text-danger" onClick={() => { if (window.confirm("Delete this draft?")) void api<void>("DELETE", `/quotes/${q.id}`).then(() => navigate("/quotes")); }}>Delete</button>}
        </>} />
      {/* The editor's line table needs the full width on laptops; the side panels move below. */}
      <div className="grid gap-6 2xl:grid-cols-4">
        <div className="min-w-0 space-y-6 2xl:col-span-3"><ApprovalPanel quote={q} />{isDraft && canEdit && q.is_latest ? <Editor key={q.id + q.updated_at} quote={q} /> : <View quote={q} />}</div>
        <aside className="space-y-6">
          {!isDraft && q.is_latest && <PaymentPanel doc="quote" id={q.id} currency={q.currency} canEdit={canEdit} open={["sent", "accepted"].includes(q.status)} />}
          {!isDraft && q.is_latest && <EsignPanel doc="quote" id={q.id} canEdit={canEdit} open={q.status === "sent"} />}
          <section className="card p-5" aria-label="Versions">
            <h2 className="mb-3 font-semibold">Versions</h2>
            <ul className="space-y-2 text-sm">
              {q.versions.map((v) => (
                <li key={v.id} className="flex items-center justify-between gap-2">
                  <Link to={`/quotes/${v.id}`} className={v.id === q.id ? "font-semibold" : "text-brand hover:underline"}>Rev. {v.version}</Link>
                  <span className="text-xs tabular-nums text-muted">{money(v.grand_total, v.currency)}</span>
                  <QuoteStatusBadge status={v.is_latest ? v.display_status : "superseded"} />
                </li>
              ))}
            </ul>
          </section>
          <section className="card p-5" aria-label="Emails">
            <h2 className="mb-3 font-semibold">Emails</h2>
            {q.emails.length === 0 ? <p className="text-sm text-muted">Not sent yet.</p> : (
              <ul className="space-y-3 text-sm">
                {q.emails.map((e, i) => (
                  <li key={i}><p className="font-medium">{e.to_addresses.join(", ")}</p>
                    <p className="text-xs text-muted">{e.status === "sent" ? `Delivered ${formatDate(e.sent_at!)}` : e.status === "queued" ? "Queued for sending" : "Failed to send"}</p></li>
                ))}
              </ul>
            )}
          </section>
        </aside>
      </div>
      {sending && <SendQuoteDialog quote={q} onClose={() => setSending(false)} />}
    </>
  );
}
